import { createFileRoute, Link } from '@tanstack/react-router';
import { AdminLayout } from '../components/AdminLayout.js';
import { DataCard } from '../components/DataCard.js';
import { ReviewActions } from '../components/ReviewActions.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { Button } from '../components/ui/button.js';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card.js';
import { useState } from 'react';
import { StaffClientEditForm } from '../components/forms/StaffClientEditForm.js';
import { useImpersonation } from '../hooks/useImpersonation.js';
import { requireRole } from '../lib/auth-guard.js';
import { formatAddress, formatBoolean, formatPeople } from '../lib/profile-formatters.js';
import { trpc } from '../lib/trpc.js';

export const Route = createFileRoute('/admin/clients/$id')({
  component: ClientDetail,
  beforeLoad: async ({ context }) => {
    await requireRole(context.queryClient, ['ceo', 'internal_staff']);
  },
});

function ClientDetail() {
  const { id } = Route.useParams();
  const utils = trpc.useUtils();
  const impersonation = useImpersonation();
  const [editing, setEditing] = useState(false);
  const client = trpc.clients.getById.useQuery({ clientId: id });
  const docs = trpc.documents.listForOwner.useQuery({ ownerType: 'client', ownerId: id });
  const review = trpc.clients.review.useMutation({
    onSuccess: () => {
      utils.clients.getById.invalidate({ clientId: id });
      utils.clients.list.invalidate();
      utils.admin.overview.invalidate();
    },
  });
  const reviewDoc = trpc.documents.review.useMutation({
    onSuccess: () => utils.documents.listForOwner.invalidate({ ownerType: 'client', ownerId: id }),
  });

  if (client.isLoading)
    return (
      <AdminLayout>
        <p className="text-[var(--muted-foreground)]">Lade …</p>
      </AdminLayout>
    );

  if (client.error || !client.data)
    return (
      <AdminLayout>
        <p className="text-[oklch(0.55_0.2_25)]">
          Fehler: {client.error?.message ?? 'Client nicht gefunden'}
        </p>
      </AdminLayout>
    );

  const c = client.data;

  return (
    <AdminLayout>
      <div className="space-y-6">
        <header>
          <Link
            to="/admin/clients"
            className="text-sm text-[var(--muted-foreground)] hover:text-foreground"
          >
            ← Kunden-Liste
          </Link>
          <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-3xl font-bold">{c.companyName ?? '(unbenannt)'}</h1>
              <p className="text-[var(--muted-foreground)]">
                Status: <StatusBadge status={c.status} /> · zuletzt geändert{' '}
                {new Date(c.updatedAt).toLocaleString('de-DE')}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={impersonation.starting}
              onClick={() => impersonation.startClient(c.id)}
              title="Die Sicht dieses Kunden übernehmen (auditiert)"
            >
              {impersonation.starting ? 'Bereite Kundensicht vor …' : '↪ Kundensicht übernehmen'}
            </Button>
          </div>
        </header>

        <Card>
          <CardHeader>
            <CardTitle>Profil-Review</CardTitle>
            <CardDescription>Firmenprofil freigeben, ablehnen oder blockieren</CardDescription>
          </CardHeader>
          <CardContent>
            <ReviewActions
              currentStatus={c.status}
              pending={review.isPending}
              onReview={(action, notes) => review.mutateAsync({ clientId: c.id, action, notes })}
            />
            {c.reviewNotes && (
              <p className="mt-3 text-xs text-[var(--muted-foreground)]">
                Letzte Notiz: {c.reviewNotes}
              </p>
            )}
          </CardContent>
        </Card>

        <div className="grid gap-4 md:grid-cols-2">
          <DataCard
            title="Firma"
            rows={[
              ['Firmenname', c.companyName],
              ['Rechtsform', c.legalForm],
              [
                'Sitz',
                formatAddress(
                  c.registeredAddressStreet,
                  c.registeredAddressZip,
                  c.registeredAddressCity,
                ),
              ],
              ['HRB', c.hrbNumber],
              ['Amtsgericht', c.districtCourt],
              ['USt-ID', c.vatId],
              ['Geschäftsführung', formatPeople(c.managingDirectors)],
              ['Zeichnungsberechtigte', formatPeople(c.authorizedSignatories)],
            ]}
          />
          <DataCard
            title="Billing"
            rows={[
              ['Rechnungs-E-Mail', c.billingEmail],
              ['Abweichende Adresse', formatBoolean(c.billingAddressDifferent)],
              [
                'Rechnungsadresse',
                c.billingAddressDifferent
                  ? formatAddress(c.billingAddressStreet, c.billingAddressZip, c.billingAddressCity)
                  : null,
              ],
              ['Zahlungsziel (Tage)', c.paymentTermsDays?.toString()],
              ['Skonto', c.earlyPaymentDiscount],
              ['PO-Nr. Pflicht', formatBoolean(c.requirePoNumber)],
              ['Kostenstelle Pflicht', formatBoolean(c.requireCostCenter)],
            ]}
          />
          <DataCard
            title="Einsatzbetrieb"
            rows={[
              ['Zeiterfassung', c.timeTrackingMethod],
              ['Vor-Ort-Kontakte', formatPeople(c.onsiteContacts)],
              ['Interne Notizen', c.notes],
            ]}
          />
          <DataCard
            title="System & Review"
            rows={[
              ['Client-ID', c.id],
              ['Login-User', c.userId],
              ['Kundensicht-User', c.impersonationUserId],
              ['Tenant-ID', c.tenantId],
              ['HireBotIQ-Company-ID', c.hirebotiqCompanyId],
              [
                'Eingereicht',
                c.submittedAt ? new Date(c.submittedAt).toLocaleString('de-DE') : null,
              ],
              ['Geprüft', c.reviewedAt ? new Date(c.reviewedAt).toLocaleString('de-DE') : null],
              ['Geprüft durch', c.reviewedBy],
              ['Review-Notiz', c.reviewNotes],
              ['Angelegt', new Date(c.createdAt).toLocaleString('de-DE')],
            ]}
          />
        </div>

        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <div>
              <CardTitle>Daten verwalten</CardTitle>
              <CardDescription>
                Firmen-Stammdaten im Namen des Kunden bearbeiten (auditiert)
              </CardDescription>
            </div>
            <Button
              variant={editing ? 'ghost' : 'outline'}
              size="sm"
              onClick={() => setEditing((v) => !v)}
            >
              {editing ? 'Schließen' : 'Bearbeiten'}
            </Button>
          </CardHeader>
          {editing && (
            <CardContent>
              <StaffClientEditForm clientId={c.id} />
            </CardContent>
          )}
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Dokumente</CardTitle>
            <CardDescription>Pro Dokument: freigeben oder ablehnen</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {docs.data?.length === 0 && (
              <p className="text-xs text-[var(--muted-foreground)]">Keine Dokumente hochgeladen.</p>
            )}
            {docs.data?.map((d) => (
              <div
                key={d.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-[var(--border)] p-3"
              >
                <div className="min-w-0 space-y-0.5">
                  <p className="text-sm font-medium truncate">{d.originalFilename}</p>
                  <p className="text-xs text-[var(--muted-foreground)] flex items-center gap-2">
                    <span>{d.type}</span>
                    <StatusBadge status={d.status} />
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <a
                    href={`/api/download/${d.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs underline text-[var(--muted-foreground)] hover:text-foreground"
                  >
                    Ansehen
                  </a>
                  <Button
                    size="sm"
                    disabled={reviewDoc.isPending || d.status === 'approved'}
                    onClick={() => reviewDoc.mutate({ documentId: d.id, action: 'approve' })}
                  >
                    Freigeben
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={reviewDoc.isPending || d.status === 'rejected'}
                    onClick={() => reviewDoc.mutate({ documentId: d.id, action: 'reject' })}
                  >
                    Ablehnen
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
